import { Component, Input,EventEmitter, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import moment = require('moment');
import { ConfigService } from '../../service/config.service';
import { ErrorHandlingComponent } from '../../error-handling.component';
import { CardBinDetails, CardCategory, CardTypeCode, Month, PaymentCollectionRequest } from '../../model/payment/PaymentCollectionRequest';
import { PaymentCollectionService } from '../../service/payment-collection.service';
import { Keys, StorageService } from '../../service/storage.service';

@Component({
  selector: 'app-payment-mode-debit-card',
  templateUrl: './payment-mode-debit-card.component.html',
  styleUrls: ['./payment-mode-debit-card.component.scss']
})
export class PaymentModeDebitCardComponent extends ErrorHandlingComponent implements OnInit {
  public cardNumberWithSpace: string;

  @Output() isFormInstanceCreated: EventEmitter<any> = new EventEmitter<any>();
  public debitCardForm: FormGroup;
  public isVisaCard: boolean = false;
  public isMasterCard: boolean = false;
  public isRuPayCard: boolean = false;
  public isMaestroCard: boolean = false;
  public isSbiMaestroCard: boolean = false;

  public payUConvenienceFeeGSTPercentage: number;
  private debitCardFirstSixDigitPrevious: string = '';
  private paymentCollectionService: PaymentCollectionService;
  private cardBinDetails: CardBinDetails;
  private isUserLoggedIn: string;

  @Input()
  paymentRequest: PaymentCollectionRequest;

  public months = [
    { value: Month.JANUARY, displayName: 'January' },
    { value: Month.FEBRUARY, displayName: 'February' },
    { value: Month.MARCH, displayName: 'March' },
    { value: Month.APRIL, displayName: 'April' },
    { value: Month.MAY, displayName: 'May' },
    { value: Month.JUNE, displayName: 'June' },
    { value: Month.JULY, displayName: 'July' },
    { value: Month.AUGUST, displayName: 'August' },
    { value: Month.SEPTEMBER, displayName: 'September' },
    { value: Month.OCTOBER, displayName: 'October' },
    { value: Month.NOVEMBER, displayName: 'November' },
    { value: Month.DECEMBER, displayName: 'December' },
  ];

  public years: {value: string, displayName: number}[];

  constructor(formBuilder: FormBuilder, private configService: ConfigService, paymentCollectionService: PaymentCollectionService, private storageService:StorageService) {
    super();
    this.paymentCollectionService = paymentCollectionService;
    this.debitCardForm = formBuilder.group({
      cardHolderName: formBuilder.control(null, [Validators.required]),
      cardNumber: formBuilder.control(null, [Validators.required, Validators.pattern("^[0-9 ]*"), Validators.minLength(11)]),
      cardDateValidityForm : formBuilder.group({
        cardExpiryMonth: formBuilder.control(null, [Validators.required]),
        cardExpiryYear: formBuilder.control(null, [Validators.required]),
      }, {validators: PaymentModeDebitCardComponent.cardDateValidity}),
      cardCVV: formBuilder.control(null, [Validators.required, Validators.minLength(3), Validators.pattern("^[0-9]*")]),
      rememberCard: formBuilder.control(null)
    });
    this.isUserLoggedIn = this.storageService.getItem(Keys.TOKEN);
  }

  private static cardDateValidity(control: AbstractControl): void {
    const formsMonth = control.get('cardExpiryMonth').value;
    const formsYear = control.get('cardExpiryYear').value;
    const todaysMonth = moment().month() + 1;
    const todaysYear = moment().year();
    if(control.get('cardExpiryMonth').hasError('required') == false){
      if(PaymentModeDebitCardComponent.validateDate( todaysMonth, todaysYear, formsMonth, formsYear)){
        control.get('cardExpiryMonth').setErrors(null);
      }else{
        control.get('cardExpiryMonth').setErrors({match: 'invalid'});
      }
    }
  }

  private static validateDate( todaysMonth, todaysYear, formsMonth, formsYear): boolean {
    if (todaysYear == formsYear) {
      if (todaysMonth > formsMonth) {
        return false;
      }
    }
    return true;
  }

  async ngOnInit(): Promise<void> {
    this.getpayUConvenienceFee();
    await this.initializeForm();
    this.years = this.getYears();
  }

  public async initializeForm() {
    this.paymentRequest.paymentDetails.cardDetails = {
      cardHolderName: null,
      cardNumber: null,
      cardExpiryMonth: null,
      cardExpiryYear: null,
      cardCVV: null,
      cardType: null,
      rememberCard: false
    };
    this.isFormInstanceCreated.emit(this.debitCardForm);
  }
  
  private getYears() {
    const currentYear = moment().year();
    const years = Array.from({length: this.configService.getPayUNumberOfExpiryYearsForDebitCard()}, (_, i) => {
      return {value: `${currentYear + i}`, displayName: currentYear + i}
      })
    return years;
  }

  protected errorMessages(): object {
    return {
      cardHolderName: [{
            type: 'required',
            message: 'Please enter the cardholder name'
        }],
        cardNumber: [{
          type: 'cardCategoryError',
            message: 'Only debit card accepted'
        },
        {
          type: 'minlength',
          message: 'Please enter a valid card Number'
        },
        {
          type: 'pattern',
          message: 'Please enter a valid card Number'
        },
        {
          type: 'invalid',
          message: 'Please enter a valid card Number'
        },
        {
          type: 'masterCardError',
          message: 'Mastercard is coming soon, Please try with VISA or RuPay'
        },
        {
          type: 'required',
          message: 'Please enter a valid card Number'
        }],
        cardExpiryMonth: [{
            type: 'required',
            message: 'Please select a valid expiry month'
        },
        {
          type: 'match',
          message: 'Your card is expired.'
        }],
        cardExpiryYear: [{
            type: 'required',
            message: 'Please select a valid expiry year'
        }],
        cardCVV: [{
          type: 'required',
          message: 'Please enter a valid CVV'
        },
        {
          type: 'minlength',
          message: 'CVV should be of 3 digit length'
        },
        {
          type: 'pattern',
          message: 'CVV can only be in digit'
        }],
    };
  }

  private resetCardType() {
    this.isRuPayCard = false;
    this.isMasterCard = false;
    this.isVisaCard = false;
    this.isMaestroCard = false;
    this.isSbiMaestroCard = false;
  }

  private setCardType(cardType) {
    this.resetCardType();
    if(cardType === CardTypeCode.RUPAY) {
      this.isRuPayCard = true;
    } else if(cardType === CardTypeCode.VISA) {
      this.isVisaCard = true;
    } else if(cardType === CardTypeCode.MAST) {
      this.isMasterCard = true;
    } else if(cardType === CardTypeCode.MAES) {
      this.isMaestroCard = true;
    } else if(cardType === CardTypeCode.SMAE) {
      this.isSbiMaestroCard = true;
    }
  }

  public async onCardNumberChange() {
    let cardNumber =  this.cardNumberWithSpace;
    cardNumber = cardNumber?.replace(/ /g, '');
    cardNumber = cardNumber?.substr(0, 16);

    if(cardNumber?.length < 6) {
      this.resetCardType();
    }

    let cardNumberFirstSixDigitCurrent = cardNumber?.substr(0, 6);
    this.paymentRequest.paymentDetails.cardDetails.cardNumber = cardNumber;

    if (cardNumber?.length >= 6) {
      if(this.cardBinDetails?.cardType){
        this.setCardType(this.cardBinDetails?.cardType);
      }
      if (this.debitCardFirstSixDigitPrevious != cardNumberFirstSixDigitCurrent) {
        this.debitCardFirstSixDigitPrevious = cardNumberFirstSixDigitCurrent;
        try {
          if(this.isUserLoggedIn) {
            this.cardBinDetails = await this.paymentCollectionService.getCardBinDetails(cardNumberFirstSixDigitCurrent);
          } else {
            const hashedProfileId = this.storageService.getItem(Keys.HASHED_PROFILE_ID);
            const ifaPaymentLinkUuid = this.storageService.getItem(Keys.IFA_PAYMENT_LINK_UUID);
            this.cardBinDetails = await this.paymentCollectionService.getCardBinDetailsForPaymentLink(cardNumberFirstSixDigitCurrent,ifaPaymentLinkUuid,hashedProfileId);
          }
          if(this.cardBinDetails.cardCategory === CardCategory.DC) {
            this.paymentRequest.paymentDetails.cardDetails.cardType = this.cardBinDetails.cardType;
          } else {
            this.paymentRequest.paymentDetails.cardDetails.cardType = null;
          }
          this.setCardType(this.cardBinDetails?.cardType);
        } catch(e) {}
      }
      if (this.cardBinDetails?.cardCategory === CardCategory.DC && this.cardBinDetails?.cardType === CardTypeCode.MAST) {
        this.debitCardForm.controls['cardNumber'].setErrors({'masterCardError': true});
      }
      if (this.cardBinDetails?.cardCategory === CardCategory.CC) {
        this.debitCardForm.controls['cardNumber'].setErrors({'cardCategoryError': true});
      } else if(this.cardBinDetails?.cardCategory != CardCategory.DC) {
        this.debitCardForm.controls['cardNumber'].setErrors({'invalid': true});
      }
    }
  }
  public onDateChange(){
    if(this.debitCardForm.get('cardDateValidityForm').valid) {
      this.paymentRequest.paymentDetails.cardDetails.cardExpiryMonth = this.debitCardForm.get('cardDateValidityForm').value.cardExpiryMonth;
      this.paymentRequest.paymentDetails.cardDetails.cardExpiryYear = this.debitCardForm.get('cardDateValidityForm').value.cardExpiryYear;
    } else {
      this.paymentRequest.paymentDetails.cardDetails.cardExpiryMonth = null;
      this.paymentRequest.paymentDetails.cardDetails.cardExpiryYear = null;
    }
  }
  
  public isDateEmpty(): boolean {
    return !this.debitCardForm['controls'].cardDateValidityForm['controls'].cardExpiryYear.value;
  }

  public getpayUConvenienceFee() {
    const payUConvenienceFeeOfDebitCard =  this.configService.getPayUConvenienceFeeOfDebitCard();
    this.payUConvenienceFeeGSTPercentage =  this.configService.getPayUConvenienceFeeGSTPercentage();
    return payUConvenienceFeeOfDebitCard * (1 + this.payUConvenienceFeeGSTPercentage / 100);
  }
}
